import { Clock, Save, Trash2, Play, X } from 'lucide-react';

interface ModalShellProps {
  title: string;
  children: React.ReactNode;
  onClose?: () => void;
}

function ModalShell({ title, children, onClose }: ModalShellProps) {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-surface border border-line rounded-2xl p-5 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 className="text-base font-semibold text-fg">{title}</h2>
          {onClose && (
            <button onClick={onClose} className="text-fg-faint hover:text-fg-muted transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

interface ResumePracticeModalProps {
  answeredCount: number;
  totalQuestions: number;
  onResume: () => void;
  onRestart: () => void;
}

export function ResumePracticeModal({ answeredCount, totalQuestions, onResume, onRestart }: ResumePracticeModalProps) {
  return (
    <ModalShell title="Session en cours">
      <p className="text-sm text-fg-subtle mb-1">
        Vous avez une session de pratique non terminée.
      </p>
      <p className="text-xs text-fg-faint mb-5 flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5" />
        Progression : {answeredCount}/{totalQuestions} questions répondues
      </p>
      <div className="flex flex-col gap-2">
        <button
          onClick={onResume}
          className="w-full py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:from-green-700 hover:to-green-800 transition-all"
        >
          <Play className="w-4 h-4" />
          Continuer la session
        </button>
        <button
          onClick={onRestart}
          className="w-full py-3 bg-overlay-1 border border-line text-fg-muted rounded-xl font-medium text-sm hover:bg-overlay-2 transition-colors"
        >
          Recommencer à zéro
        </button>
      </div>
    </ModalShell>
  );
}

interface QuitPracticeModalProps {
  answeredCount: number;
  totalQuestions: number;
  onContinue: () => void;
  onSaveAndLeave: () => void;
  onLeaveWithoutSaving: () => void;
}

export function QuitPracticeModal({
  answeredCount,
  totalQuestions,
  onContinue,
  onSaveAndLeave,
  onLeaveWithoutSaving,
}: QuitPracticeModalProps) {
  return (
    <ModalShell title="Quitter la session ?" onClose={onContinue}>
      <p className="text-sm text-fg-subtle mb-1">
        Vous êtes en plein exercice ({answeredCount}/{totalQuestions} répondues).
      </p>
      <p className="text-xs text-fg-faint mb-5">
        Si vous quittez sans enregistrer, votre progression sera perdue.
      </p>
      <div className="flex flex-col gap-2">
        <button
          onClick={onContinue}
          className="w-full py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-semibold text-sm hover:from-green-700 hover:to-green-800 transition-all"
        >
          Continuer le quiz
        </button>
        <button
          onClick={onSaveAndLeave}
          className="w-full py-3 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-xl font-medium text-sm flex items-center justify-center gap-2 hover:bg-blue-500/15 transition-colors"
        >
          <Save className="w-4 h-4" />
          Enregistrer pour plus tard
        </button>
        <button
          onClick={onLeaveWithoutSaving}
          className="w-full py-2.5 text-red-400/70 text-xs font-medium flex items-center justify-center gap-1.5 hover:text-red-400 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Quitter sans enregistrer
        </button>
      </div>
    </ModalShell>
  );
}
